import { toCanvas } from 'html-to-image';
import type { DeclaredScreenshot, ScreenshotWindow } from '@myrewards/contracts';
import { FEEDBACK_LIMITS, type CAPTURE_REASONS } from '@myrewards/domain';
import './capture.css';

// APP-104 §5.3: redraw the page as a PNG without asking the person for anything. html-to-image
// 1.11.13 was chosen by the spike (no CSP violations, fonts right, nothing left in the live DOM).

export const CAPTURING_CLASS = 'feedback-capturing';

type Reason = keyof typeof CAPTURE_REASONS;

export type CapturedScreen =
  | { state: 'captured'; bytes: Uint8Array; declared: DeclaredScreenshot }
  | { state: 'unavailable'; declared: DeclaredScreenshot };

class CaptureFailure extends Error {
  constructor(readonly reason: Reason) {
    super(reason);
  }
}

/** The picture can't be made at this size here (a limit of the browser's canvas). */
class OverCap extends CaptureFailure {
  constructor() {
    super('too_many_pixels');
  }
}

const unavailable = (reason: Reason): CapturedScreen => ({
  state: 'unavailable',
  declared: { state: 'unavailable', reason },
});

/** Width and height from the PNG header; null when it isn't a PNG. */
function pngSize(bytes: Uint8Array): { width: number; height: number } | null {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length < 24 || sig.some((b, i) => bytes[i] !== b)) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const width = v.getUint32(16);
  const height = v.getUint32(20);
  return width > 0 && height > 0 ? { width, height } : null;
}

/** A slow font or image must not leave the page blanked: give up after this long. */
export const CAPTURE_TIMEOUT_MS = 12_000;

function withTimeout<T>(work: Promise<T>, deadline: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => {
        reject(new CaptureFailure('failed'));
      },
      Math.max(0, deadline - Date.now()),
    );
  });
  return Promise.race([work, timeout]).finally(() => {
    clearTimeout(timer);
  });
}

const CLEAR = /^(?:transparent|rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\))$/i;

/** The body's colour, else the root's, else the app's page-background token. */
export function pageBackground(): string {
  for (const el of [document.body, document.documentElement]) {
    const c = getComputedStyle(el).backgroundColor.trim();
    if (c !== '' && !CLEAR.test(c)) return c;
  }
  const token = getComputedStyle(document.documentElement)
    .getPropertyValue('--surface-ground')
    .trim();
  // A colour keyword, not a raw hex value (src/design/no-raw-hex.test.ts).
  return token !== '' ? token : 'white';
}

function bytesOf(blob: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(new Uint8Array(reader.result as ArrayBuffer));
    };
    reader.onerror = () => {
      reject(new CaptureFailure('unreadable'));
    };
    reader.readAsArrayBuffer(blob);
  });
}

/** The whole page and the part of it the person has on screen, in CSS pixels. */
interface PageGeometry {
  width: number;
  height: number;
  window: ScreenshotWindow;
}

function pageGeometry(): PageGeometry {
  const root = document.documentElement;
  const width = Math.max(root.scrollWidth, window.innerWidth, 1);
  const height = Math.max(root.scrollHeight, window.innerHeight, 1);
  const x = Math.min(Math.max(0, Math.round(window.scrollX)), Math.max(0, width - 1));
  const y = Math.min(Math.max(0, Math.round(window.scrollY)), Math.max(0, height - 1));
  return {
    width,
    height,
    window: {
      x,
      y,
      width: Math.min(window.innerWidth, width - x),
      height: Math.min(window.innerHeight, height - y),
    },
  };
}

/** The app's focus colour, else its accent, else a plain keyword (no raw hex in the code). */
function outlineColour(): string {
  const style = getComputedStyle(document.documentElement);
  for (const token of ['--focus-ring-color', '--accent']) {
    const value = style.getPropertyValue(token).trim();
    if (value !== '') return value;
  }
  return 'teal';
}

/** How wide the outline is, in CSS pixels, and its white inner edge. */
const OUTLINE_PX = 3;
const EDGE_PX = 1;

/**
 * A clear outline around the window the person had on screen: the accent colour, with a thin
 * white edge just inside it for contrast. Drawn inside the window's own edge, so it never leaves
 * the picture. `rect` is in picture pixels.
 */
function outline(canvas: HTMLCanvasElement, rect: ScreenshotWindow, ratio: number): void {
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new CaptureFailure('failed');
  const line = OUTLINE_PX * ratio;
  const edge = EDGE_PX * ratio;
  ctx.save();
  ctx.lineJoin = 'miter';
  ctx.strokeStyle = outlineColour();
  ctx.lineWidth = line;
  ctx.strokeRect(rect.x + line / 2, rect.y + line / 2, rect.width - line, rect.height - line);
  ctx.strokeStyle = 'white';
  ctx.lineWidth = edge;
  ctx.strokeRect(
    rect.x + line + edge / 2,
    rect.y + line + edge / 2,
    rect.width - 2 * line - edge,
    rect.height - 2 * line - edge,
  );
  ctx.restore();
}

/** What a redraw covers: all of the page, or the rows between `top` and `top + height`. */
interface Slice {
  top: number;
  height: number;
}

interface Drawn {
  bytes: Uint8Array;
  window: ScreenshotWindow;
}

function encode(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });
}

/**
 * Draws the page (or a slice of it) at this ratio, outlines the window in it and encodes the PNG.
 * All of it must finish by `deadline` (the timeout belongs to the capture, not to each try).
 */
async function redraw(
  page: PageGeometry,
  slice: Slice,
  pixelRatio: number,
  deadline: number,
): Promise<Drawn> {
  const root = document.documentElement;
  root.classList.add(CAPTURING_CLASS);
  let canvas: HTMLCanvasElement | undefined;
  try {
    canvas = await withTimeout(
      toCanvas(root, {
        width: page.width,
        height: slice.height,
        pixelRatio,
        // html-to-image rescales any side over 16,384 px unless told not to; a rescaled picture
        // would put the window in the wrong place, so the size asked for is the size drawn.
        skipAutoScale: true,
        // The area below <body> is left white unless this is given.
        backgroundColor: pageBackground(),
        // A slice is the full-height page moved up inside a window of the slice's height, so the
        // layout (min-heights, footer position, viewport units) is the one the person saw.
        ...(slice.height < page.height && {
          style: {
            height: `${String(page.height)}px`,
            transform: `translateY(${String(-slice.top)}px)`,
          },
        }),
        filter: (node) => !(node instanceof Element && node.closest('[data-feedback-ui]') !== null),
      }),
      deadline,
    );
    // The browser may have made a different size (a limit of its own): never record a window in
    // other coordinates. It counts as over the cap, so the next fallback takes over.
    if (
      canvas.width !== Math.floor(page.width * pixelRatio) ||
      canvas.height !== Math.floor(slice.height * pixelRatio)
    )
      throw new OverCap();
    // The window, in this picture's pixels, clamped to the picture.
    const x = Math.round(page.window.x * pixelRatio);
    const y = Math.round((page.window.y - slice.top) * pixelRatio);
    const rect = {
      x,
      y,
      width: Math.min(Math.round(page.window.width * pixelRatio), canvas.width - x),
      height: Math.min(Math.round(page.window.height * pixelRatio), canvas.height - y),
    };
    outline(canvas, rect, pixelRatio);
    const blob = await withTimeout(encode(canvas), deadline);
    if (blob === null) throw new CaptureFailure('failed');
    return { bytes: await bytesOf(blob), window: rect };
  } finally {
    // Give the canvas's memory back on every path.
    if (canvas !== undefined) {
      canvas.width = 0;
      canvas.height = 0;
    }
    root.classList.remove(CAPTURING_CLASS);
  }
}

/** Which cap, if any, the picture is over. */
function overCap(bytes: Uint8Array): Reason | null {
  const size = pngSize(bytes);
  if (size === null) throw new CaptureFailure('unreadable');
  if (size.width * size.height > FEEDBACK_LIMITS.screenshotPixels) return 'too_many_pixels';
  if (bytes.byteLength > FEEDBACK_LIMITS.screenshotBytes) return 'too_large';
  return null;
}

/** The most pixels along one side that a canvas is trusted with (html-to-image's own limit). */
export const MAX_SIDE_PX = 16_384;

/** Would the slice, drawn at this ratio, stay within the caps? Known before drawing. */
const fitsPixels = (width: number, height: number, ratio: number): boolean => {
  const w = Math.floor(width * ratio);
  const h = Math.floor(height * ratio);
  return w <= MAX_SIDE_PX && h <= MAX_SIDE_PX && w * h <= FEEDBACK_LIMITS.screenshotPixels;
};

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

let inFlight: Promise<CapturedScreen> | null = null;

/** Single-flight: a second call while one is drawing gets the same result. */
export function captureScreen(): Promise<CapturedScreen> {
  inFlight ??= captureOnce().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * APP-104 §5.3, QA-059: the whole page, with the window the person had on screen outlined in it.
 * Over the caps, the same page at ratio 1; still over, a slice of it: the window and a screen of
 * page above and below (the window's place in the slice is recorded either way).
 */
async function captureOnce(): Promise<CapturedScreen> {
  try {
    const deadline = Date.now() + CAPTURE_TIMEOUT_MS;
    const page = pageGeometry();
    const whole: Slice = { top: 0, height: page.height };
    const ratios = [...new Set([Math.min(2, window.devicePixelRatio || 1), 1])];
    let drawn: Drawn | null = null;
    let over: Reason | null = 'too_many_pixels';
    for (const ratio of ratios) {
      // The pixel count is known in advance: a page that is too big isn't drawn to find out.
      if (!fitsPixels(page.width, whole.height, ratio)) continue;
      try {
        drawn = await redraw(page, whole, ratio, deadline);
      } catch (error) {
        // The browser made a different size: the next ratio, then the slice.
        if (error instanceof OverCap) continue;
        throw error;
      }
      over = overCap(drawn.bytes);
      if (over === null) break;
    }
    if (over !== null) {
      const screen = window.innerHeight;
      const top = Math.max(0, page.window.y - screen);
      const bottom = Math.min(page.height, page.window.y + page.window.height + screen);
      const slice: Slice = { top, height: bottom - top };
      if (!fitsPixels(page.width, slice.height, 1)) return unavailable('too_many_pixels');
      drawn = await redraw(page, slice, 1, deadline);
      over = overCap(drawn.bytes);
    }
    if (over !== null || drawn === null) return unavailable(over ?? 'failed');
    const size = pngSize(drawn.bytes);
    if (size === null) return unavailable('unreadable');
    return {
      state: 'captured',
      bytes: drawn.bytes,
      declared: {
        state: 'pending_upload',
        method: 'page-redraw',
        width: size.width,
        height: size.height,
        bytes: drawn.bytes.byteLength,
        sha256: await sha256Hex(drawn.bytes),
        window: fitWindow(drawn.window, size),
      },
    };
  } catch (error) {
    // Only a fixed reason leaves here; the thrown message never does.
    return unavailable(error instanceof CaptureFailure ? error.reason : 'failed');
  }
}

/** The window as declared: whole pixels, never outside the picture that was really made. */
function fitWindow(
  w: ScreenshotWindow,
  picture: { width: number; height: number },
): ScreenshotWindow {
  const x = Math.min(Math.max(0, w.x), picture.width - 1);
  const y = Math.min(Math.max(0, w.y), picture.height - 1);
  return {
    x,
    y,
    width: Math.max(1, Math.min(w.width, picture.width - x)),
    height: Math.max(1, Math.min(w.height, picture.height - y)),
  };
}
